import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { LogIn, LogOut, Coffee, Play, CheckCircle2, Clock } from "lucide-react";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/ui";
import { formatDuration } from "../../utils/duration";

const AttendanceCard = ({ todayRec, onMarkIn, onMarkOut, refresh }) => {
  const [now, setNow] = useState(new Date());
  const [breakElapsed, setBreakElapsed] = useState(0); // seconds since break started
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const token = localStorage.getItem("token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  // No trailing slash — so we can append /start-break etc. cleanly
  const apiBase = `${API_URL}/api/attendance`;

  /* ---- LIVE CLOCK ---- */
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  /* ---- BREAK ELAPSED TIMER ---- */
  useEffect(() => {
    const onBreak = Number(todayRec?.on_break) === 1;
    if (!onBreak || !todayRec?.active_break_start) {
      setBreakElapsed(0);
      return;
    }
    const breakStart = new Date(
      String(todayRec.active_break_start).replace(" ", "T")
    );
    const tick = () => {
      setBreakElapsed(Math.floor((new Date() - breakStart) / 1000));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [todayRec?.on_break, todayRec?.active_break_start]);

  /* ---- DERIVED STATE ---- */
  const status = !todayRec
    ? "not-marked-in"
    : todayRec.mark_in && !todayRec.mark_out
    ? "in-progress"
    : "present";

  const activeBreak = Number(todayRec?.on_break) === 1;
  const breakSeconds = Number(todayRec?.break_seconds || 0);

  /* ---- WORKING HOURS (live) ---- */
  const workingSeconds = useCallback(() => {
    if (!todayRec?.mark_in) return 0;
    const start = new Date(String(todayRec.mark_in).replace(" ", "T"));
    const end = todayRec.mark_out
      ? new Date(String(todayRec.mark_out).replace(" ", "T"))
      : now;
    const totalBreak = breakSeconds + (activeBreak ? breakElapsed : 0);
    return Math.max(Math.floor((end - start) / 1000) - totalBreak, 0);
  }, [todayRec, now, breakSeconds, breakElapsed, activeBreak]);

  /* ---- DATE / TIME DISPLAY ---- */
  const liveTime = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const liveDate = now.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(String(iso).replace(" ", "T"));
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  };

  /* ---- BREAK API ---- */
  const startBreak = async () => {
    if (!todayRec?.id) {
      setErrorMsg("No attendance record found. Please mark in first.");
      return;
    }
    setActionLoading(true);
    setErrorMsg(null);
    try {
      await axios.post(
        `${apiBase}/start-break`,
        { attendance_id: todayRec.id },
        authHeader
      );
      await refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to start break. Please try again.";
      setErrorMsg(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const endBreak = async () => {
    if (!todayRec?.id) {
      setErrorMsg("No attendance record found.");
      return;
    }
    setActionLoading(true);
    setErrorMsg(null);
    try {
      await axios.post(
        `${apiBase}/end-break`,
        { attendance_id: todayRec.id },
        authHeader
      );
      await refresh();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to end break. Please try again.";
      setErrorMsg(msg);
    } finally {
      setActionLoading(false);
    }
  };

  /* ---- RENDER (EM design hero card) ---- */
  const pillBase = { display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 9999, font: `600 12px ${T.font}` };
  const actionBtn = (bg, color, shadow) => ({ padding: "13px 22px", borderRadius: 12, border: 0, cursor: actionLoading ? "default" : "pointer", background: bg, color, font: `700 14px ${T.font}`, display: "flex", alignItems: "center", gap: 8, justifyContent: "center", boxShadow: shadow || "none", opacity: actionLoading ? 0.7 : 1 });
  const statBox = (label, value, color) => (
    <div style={{ padding: "10px 16px", borderRadius: 10, background: "#fafafa", border: `1px solid ${T.divider}`, flex: 1 }}>
      <div style={{ font: `500 10px ${T.font}`, color: T.fg7, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ font: `700 16px ${T.font}`, color: color || T.fg1, marginTop: 2 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowSm, padding: 28 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "start" }}>
        {/* LEFT — date + live clock + status */}
        <div>
          <div style={{ font: `500 13px ${T.font}`, color: T.fg5, marginBottom: 4 }}>{liveDate}</div>
          <div style={{ font: `800 46px/1 ${T.font}`, letterSpacing: "-0.04em", color: T.orange, fontVariantNumeric: "tabular-nums" }}>{liveTime}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
            {status === "not-marked-in" && <span style={{ ...pillBase, background: T.bgMuted, color: T.fg5 }}><Clock size={12} /> Not Marked</span>}
            {status === "in-progress" && !activeBreak && (
              <span style={{ ...pillBase, background: T.success50, color: T.success600 }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: T.success600, animation: "emPulse 1.5s ease-in-out infinite", display: "inline-block" }} />
                Working · {formatDuration(workingSeconds())}
              </span>
            )}
            {activeBreak && <span style={{ ...pillBase, background: "rgba(251,191,36,0.14)", color: "#b45309" }}><Coffee size={12} /> On Break · {formatDuration(breakElapsed)}</span>}
            {status === "present" && <span style={{ ...pillBase, background: T.info50, color: T.info600 }}><CheckCircle2 size={12} /> Day Complete</span>}
          </div>
        </div>

        {/* RIGHT — actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 220 }}>
          {errorMsg && (
            <div role="alert" style={{ padding: "8px 12px", borderRadius: 10, background: T.danger50, border: "1px solid #fecaca", color: "#b91c1c", font: `500 12px ${T.font}`, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ flex: 1 }}>⚠ {errorMsg}</span>
              <button onClick={() => setErrorMsg(null)} style={{ background: "none", border: 0, cursor: "pointer", color: "#b91c1c", fontSize: 16 }}>×</button>
            </div>
          )}
          {status === "not-marked-in" && (
            <button onClick={onMarkIn} disabled={actionLoading} style={actionBtn(T.gradient, "#fff", "0 8px 20px -8px rgba(255,105,0,0.5)")}>
              <LogIn size={16} /> Mark In
            </button>
          )}
          {status === "in-progress" && !activeBreak && (<>
            <button onClick={startBreak} disabled={actionLoading} style={actionBtn("#fff", T.fg1)}>
              <Coffee size={14} /> {actionLoading ? "Starting…" : "Start Break"}
            </button>
            <button onClick={onMarkOut} disabled={actionLoading} style={{ ...actionBtn(T.bgBlack, "#fff"), padding: "11px 20px", font: `600 13px ${T.font}` }}>
              <LogOut size={14} /> Mark Out
            </button>
          </>)}
          {activeBreak && (
            <button onClick={endBreak} disabled={actionLoading} style={actionBtn("linear-gradient(135deg,#fbbf24,#f59e0b)", "#fff", "0 8px 20px -8px rgba(251,191,36,0.5)")}>
              <Play size={16} /> {actionLoading ? "Resuming…" : "Resume Work"}
            </button>
          )}
          {status === "present" && (<>
            <div style={{ padding: "12px 16px", borderRadius: 12, background: T.success50, border: "1px solid #bbf7d0", font: `500 13px ${T.font}`, color: T.success600, textAlign: "center" }}>
              <CheckCircle2 size={16} /><br />Marked out at {fmtTime(todayRec?.mark_out)}
            </div>
            {/* Marking out no longer ends the day: someone who stepped out, or
                marked out by mistake, can resume. The server reopens today's
                row and books the time away as a break, so the hours stay right. */}
            <button onClick={onMarkIn} disabled={actionLoading} style={actionBtn(T.gradient, "#fff", "0 8px 20px -8px rgba(255,105,0,0.5)")}>
              <LogIn size={16} /> {actionLoading ? "Marking in…" : "Mark In Again"}
            </button>
          </>)}
        </div>
      </div>

      {/* Stat row */}
      {status !== "not-marked-in" && (
        <div style={{ display: "flex", gap: 12, marginTop: 20, paddingTop: 18, borderTop: `1px solid ${T.divider}` }}>
          {statBox("Checked in", fmtTime(todayRec?.mark_in))}
          {statBox("Working time", formatDuration(workingSeconds()), T.success600)}
          {statBox("Break time", formatDuration(breakSeconds + (activeBreak ? breakElapsed : 0)), "#b45309")}
          {statBox(status === "present" ? "Checked out" : "Status", status === "present" ? fmtTime(todayRec?.mark_out) : (activeBreak ? "On Break" : "Working"))}
        </div>
      )}
      <style>{`@keyframes emPulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </div>
  );
};

AttendanceCard.propTypes = {
  todayRec: PropTypes.object,
  onMarkIn: PropTypes.func.isRequired,
  onMarkOut: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
};

export default AttendanceCard;
